import React, { useState } from 'react';

import { rangeLookup } from './calculatorUtils';
import css from './calculatorStyles.module.css';

// ── Yoke Adjustment ──

const YOKE_TABLE = [
  [81, 87, '-1'],
  [88, 108, 'Std'],
  [109, 123, '+1'],
  [124, 127, '+2'],
];

function calcYoke(chest) {
  return rangeLookup(chest, YOKE_TABLE);
}

// ── Leg Pads ──

const LEG_TABLE = [
  [56, 59, 14],
  [60, 64, 15],
  [65, 69, 16],
  [70, 72, 17],
  [73, 77, 18],
  [78, 80, 19],
  [81, 83, 20],
  [84, 87, 21],
  [88, 90, 22],
  [91, 93, 23],
  [94, 97, 24],
  [98, 104, 25],
];

function calcLegPads(leg) {
  return rangeLookup(leg, LEG_TABLE);
}

// ── Main Lift Web (MLW) ──

const MLW_HEIGHT_TABLE = [
  [152, 156, 14],
  [157, 158, 14.5],
  [159, 162, 15],
  [163, 166, 15.5],
  [167, 171, 16],
  [172, 176, 16.5],
  [177, 190, 17],
  [191, 197, 17.5],
  [198, 199, 18],
];

const MLW_TORSO_TABLE = [
  [35, 37, 15],
  [38, 42, 15.5],
  [43, 44, 16],
  [45, 46, 16.5],
  [47, 54, 17],
  [55, 62, 17.5],
  [63, 65, 18],
];

const MLW_GIRTH_TABLE = [
  [131, 135, 14],
  [136, 138, 14.5],
  [139, 145, 15],
  [146, 150, 15.5],
  [151, 155, 16],
  [156, 161, 16.5],
  [162, 171, 17],
  [172, 186, 17.5],
  [187, 197, 18],
];

function calcMLW(height, torso, girth, hipRings, oldStyle) {
  const values = [];

  const hVal = rangeLookup(height, MLW_HEIGHT_TABLE);
  if (hVal !== null) values.push(hVal);

  const tVal = rangeLookup(torso, MLW_TORSO_TABLE);
  if (tVal !== null) values.push(tVal);

  const gVal = rangeLookup(girth, MLW_GIRTH_TABLE);
  if (gVal !== null) values.push(gVal);

  if (values.length === 0) return null;

  let avg = values.reduce((sum, v) => sum + v, 0) / values.length;

  if (hipRings === 'low') avg += 0.5;
  if (oldStyle === 'yes') avg += 2.0;

  return Math.round(avg * 2) / 2;
}

// ── Stabilizer ──

const STABILIZER_RANGES = [
  [55, 57],
  [58, 67],
  [68, 78],
  [79, 87],
  [88, 98],
  [99, 108],
  [109, 123],
  [124, 130],
];

const STABILIZER_GROUPS = {
  A: { containers: ['MZS', 'MXS', 'M0S', 'MX', 'MT', 'M0'], values: [-1, -0.5, 0, 0.5, 1, 1.5, 2, 2.5] },
  B: { containers: ['M1', 'M2', 'M2S', 'M3', 'M4'], values: [-1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2] },
  C: { containers: ['M5', 'M6'], values: [-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5] },
  D: { containers: ['M7', 'M8'], values: [-2, -2, -1.5, -1, -0.5, 0, 0.5, 1] },
};

function calcStabilizer(waist, container) {
  let group = null;
  for (const key of Object.keys(STABILIZER_GROUPS)) {
    if (STABILIZER_GROUPS[key].containers.includes(container)) {
      group = STABILIZER_GROUPS[key];
      break;
    }
  }
  if (!group) return null;

  for (let i = 0; i < STABILIZER_RANGES.length; i++) {
    const [min, max] = STABILIZER_RANGES[i];
    if (waist >= min && waist <= max) {
      return group.values[i];
    }
  }
  return null;
}

// ── Format helpers ──

function formatValue(val) {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'string') return val;
  if (val > 0) return `+${val}`;
  return String(val);
}

// ── Container options ──

const CONTAINER_OPTIONS = ['MZS', 'MXS', 'M0S', 'MX', 'MT', 'M0', 'M1', 'M2', 'M2S', 'M3', 'M4', 'M5', 'M6', 'M7', 'M8'];

// ── Initial form state ──

const INITIAL_STATE = {
  chest: '',
  leg: '',
  hipRings: '',
  oldStyle: '',
  height: '',
  torso: '',
  girth: '',
  waist: '',
  container: '',
};

// ── Component ──

const MirageCalculator = () => {
  const [form, setForm] = useState(INITIAL_STATE);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setForm(INITIAL_STATE);
    setResults(null);
    setError('');
  };

  const handleCalculate = () => {
    setError('');
    setResults(null);

    const chest = parseInt(form.chest, 10);
    const leg = parseInt(form.leg, 10);
    const height = parseInt(form.height, 10);
    const torso = parseInt(form.torso, 10);
    const girth = parseInt(form.girth, 10);
    const waist = parseInt(form.waist, 10);

    const errors = [];
    if (isNaN(chest) || chest < 70 || chest > 140) errors.push('Chest Circumference must be between 70 and 140 cm.');
    if (isNaN(leg) || leg < 50 || leg > 110) errors.push('Leg Measurement must be between 50 and 110 cm.');
    if (isNaN(height) || height < 140 || height > 210) errors.push('Height must be between 140 and 210 cm.');
    if (isNaN(torso) || torso < 30 || torso > 70) errors.push('Torso must be between 30 and 70 cm.');
    if (isNaN(girth) || girth < 120 || girth > 210) errors.push('Long Girth must be between 120 and 210 cm.');
    if (isNaN(waist) || waist < 50 || waist > 140) errors.push('Waist must be between 50 and 140 cm.');

    if (errors.length > 0) {
      setError(errors.join(' '));
      return;
    }

    const yoke = calcYoke(chest);
    const legPads = calcLegPads(leg);
    const mlw = calcMLW(height, torso, girth, form.hipRings, form.oldStyle);
    const stabilizer = calcStabilizer(waist, form.container);

    const warnings = [];
    if (yoke === null) warnings.push('Chest value is outside the Yoke Adjustment range (81-127 cm).');
    if (legPads === null) warnings.push('Leg value is outside the Leg Pads range (56-104 cm).');
    if (mlw === null) warnings.push('Height, Torso, or Girth values are outside the MLW lookup ranges.');
    if (stabilizer === null) warnings.push('Waist value is outside the Stabilizer range (55-130 cm).');

    if (warnings.length > 0) {
      setError(warnings.join(' '));
    }

    setResults({ yoke, legPads, mlw, stabilizer });
  };

  return (
    <div className={css.calculator}>
      <div className={css.card}>
        <h4 className={css.sectionTitle}>Your Measurements</h4>

        <div className={css.grid}>
          <div className={css.field}>
            <label className={css.label} htmlFor="mc-chest">Chest Circumference (cm)</label>
            <input
              className={css.input}
              id="mc-chest"
              name="chest"
              type="number"
              min={70}
              max={140}
              value={form.chest}
              onChange={handleChange}
              placeholder="e.g., 98"
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="mc-leg">Leg Measurement (cm)</label>
            <input
              className={css.input}
              id="mc-leg"
              name="leg"
              type="number"
              min={50}
              max={110}
              value={form.leg}
              onChange={handleChange}
              placeholder="e.g., 66"
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="mc-hipRings">Hip Rings</label>
            <select
              className={css.select}
              id="mc-hipRings"
              name="hipRings"
              value={form.hipRings}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              <option value="unisyn">Unisyn Hip Rings</option>
              <option value="low">Low Hip Rings</option>
            </select>
            <div className={css.imgBox}>
              <figure className={css.figure}>
                <img src="/static/images/harness-guide/unisyn-hip-rings.webp" alt="Unisyn Hip Rings" width={400} height={300} loading="lazy" />
                <figcaption className={css.figcap}>Unisyn Hip Rings</figcaption>
              </figure>
              <figure className={css.figure}>
                <img src="/static/images/harness-guide/low-hip-rings.webp" alt="Low Hip Rings" width={400} height={300} loading="lazy" />
                <figcaption className={css.figcap}>Low Hip Rings</figcaption>
              </figure>
            </div>
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="mc-oldStyle">Old style no rings harness</label>
            <select
              className={css.select}
              id="mc-oldStyle"
              name="oldStyle"
              value={form.oldStyle}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="mc-height">Height (cm)</label>
            <input
              className={css.input}
              id="mc-height"
              name="height"
              type="number"
              min={140}
              max={210}
              value={form.height}
              onChange={handleChange}
              placeholder="e.g., 176"
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="mc-torso">Torso (cm)</label>
            <input
              className={css.input}
              id="mc-torso"
              name="torso"
              type="number"
              min={30}
              max={70}
              value={form.torso}
              onChange={handleChange}
              placeholder="e.g., 44"
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="mc-girth">Long Girth (cm)</label>
            <input
              className={css.input}
              id="mc-girth"
              name="girth"
              type="number"
              min={120}
              max={210}
              value={form.girth}
              onChange={handleChange}
              placeholder="e.g., 165"
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="mc-waist">Waist (cm)</label>
            <input
              className={css.input}
              id="mc-waist"
              name="waist"
              type="number"
              min={50}
              max={140}
              value={form.waist}
              onChange={handleChange}
              placeholder="e.g., 82"
            />
          </div>

          <div className={css.field}>
            <label className={css.label} htmlFor="mc-container">Container Size</label>
            <select
              className={css.select}
              id="mc-container"
              name="container"
              value={form.container}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              {CONTAINER_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={css.actions}>
          <button className={css.btnPrimary} type="button" onClick={handleCalculate}>
            Calculate
          </button>
          <button className={css.btnSecondary} type="button" onClick={handleReset}>
            Reset
          </button>
        </div>

        <p className={error ? `${css.error} ${css.errorVisible}` : css.error}>
          {error}
        </p>

        {results && (
          <div className={css.results}>
            <div className={css.tile}>
              <span className={css.tileTitle}>Yoke Adjustment</span>
              <div className={css.tileRight}>
                <span className={css.tileValue}>{formatValue(results.yoke)}</span>
              </div>
            </div>

            <div className={css.tile}>
              <span className={css.tileTitle}>Leg Pads</span>
              <div className={css.tileRight}>
                <span className={css.tileValue}>{formatValue(results.legPads)}</span>
              </div>
            </div>

            <div className={css.tile}>
              <span className={css.tileTitle}>Main Lift Web (MLW)</span>
              <div className={css.tileRight}>
                <span className={css.tileValue}>{formatValue(results.mlw)}</span>
              </div>
            </div>

            <div className={css.tile}>
              <span className={css.tileTitle}>Stabilizer</span>
              <div className={css.tileRight}>
                <span className={css.tileValue}>{formatValue(results.stabilizer)}</span>
              </div>
            </div>
          </div>
        )}

        <p className={css.note}>
          For MLW, we average the valid values from Height, Torso, and Long Girth. We then add
          +0.5 for Low Hip Rings, +2.0 for old style no rings harness, and round to the nearest
          0.5. "Std" means no adjustment.
        </p>
      </div>
    </div>
  );
};

export default MirageCalculator;
